/**
 * assetNaming.js — Naming convention unico per tutti i file scaricabili.
 *
 * Tutti i file (PNG, MP4, PDF, ZIP) seguono la regola:
 *
 *   pronto-{slug-immobile}-{kind}[-{layout}][-{idx}].{ext}
 *
 * Esempi:
 *   pronto-trilocale-brera-nuovo-annuncio-elegante.png
 *   pronto-trilocale-brera-scheda.pdf
 *   pronto-trilocale-brera-scheda-social.zip          (ZIP)
 *     └─ pronto-trilocale-brera-scheda-social-01.png  (slide dentro)
 *   pronto-trilocale-brera-pacchetto-social.zip
 *   pronto-trilocale-brera-reel.mp4
 *
 * Vantaggi:
 *   - L'agente li ritrova in cartella Download per nome immobile
 *   - Il prefisso `pronto-` distingue subito i file generati dall'app
 *   - Slug deterministico → nomi coerenti tra desktop/mobile/condivisioni
 */

/** Converte un titolo in slug url-safe.
 *  Es: "Trilocale ristrutturato · Brera, Milano" → "trilocale-brera-milano" */
export function slugify(text, { maxLen = 40 } = {}) {
  if (!text) return "immobile";
  const slug = String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")     // strip accents
    .replace(/[^a-z0-9\s-]/g, " ")       // strip punctuation
    .replace(/\s+/g, "-")                // spaces → -
    .replace(/-+/g, "-")                 // collapse multiple -
    .replace(/^-+|-+$/g, "");            // trim -
  return (slug || "immobile").slice(0, maxLen).replace(/-+$/, "");
}

/** Mappa "kind" interni → frammento user-friendly del filename. */
const KIND_LABELS = {
  // Post statici
  nuovo_annuncio:    "nuovo-annuncio",
  prezzo_aggiornato: "prezzo-aggiornato",
  venduto:           "venduto",
  open_house:        "open-house",
  // Story
  story:             "story",
  story_nuovo:       "story-nuovo",
  story_venduto:     "story-venduto",
  story_stima:       "story-stima",
  // Generic
  post:              "post",
  carousel:          "carosello",
  reel:              "reel",
  before_after:      "prima-dopo",
  // PDF stampabile
  brochure:          "scheda",
  brochure_social:   "scheda-social",
  // Volantino
  volantino_stima:   "volantino-stima",
  // Pack
  pack:              "pacchetto-social",
};

function kindLabel(kind) {
  return KIND_LABELS[kind] || String(kind || "asset").replace(/_/g, "-");
}

/** Build filename principale. */
export function makeAssetFilename({
  propertyTitle,
  propertyCity,
  kind,
  layout,
  ext = "png",
  idx,           // per ZIP slide (1, 2, 3, ...) o aspect "1x1"/"4x5"
  suffix,        // hook libero (es. "preview", "v2")
}) {
  // Dedupe city se già contenuta nel title (es. title="Trilocale Brera, Milano"
  // + city="Milano" → evitiamo "trilocale-brera-milano-milano" che dopo il
  // truncate produrrebbe suffissi orfani tipo "-mil").
  const titleStr = (propertyTitle || "").trim();
  const cityStr = (propertyCity || "").trim();
  const titleLower = titleStr.toLowerCase();
  const cityLower = cityStr.toLowerCase();
  const cityAlreadyInTitle = cityLower && titleLower.includes(cityLower);
  const slugSource = cityAlreadyInTitle || !cityStr
    ? titleStr || "immobile"
    : `${titleStr} ${cityStr}`.trim();
  const slug = slugify(slugSource);
  const parts = ["pronto", slug, kindLabel(kind)];
  if (layout) parts.push(slugify(layout));
  if (idx !== undefined && idx !== null && idx !== "") {
    const idxStr = typeof idx === "number" ? String(idx).padStart(2, "0") : String(idx);
    parts.push(idxStr);
  }
  if (suffix) parts.push(slugify(suffix));
  return `${parts.filter(Boolean).join("-")}.${ext}`;
}

/** Variante per i file INTERNI a uno ZIP — stesso schema del filename
 *  esterno ma senza estensione dello ZIP padre, usato come prefisso. */
export function makeZipEntryName(opts) {
  return makeAssetFilename(opts);
}
